/**
 * Session timeout hook with automatic logout and warning
 */

import { signOut } from '@lumora/common';
import { useCallback, useEffect, useRef } from 'react';

import { SessionManager } from '../utils/security';

import { useAppDispatch, useAppSelector } from './redux';

interface UseSessionTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

export const useSessionTimeout = (options: UseSessionTimeoutOptions = {}) => {
  const {
    timeoutMinutes = 30,
    warningMinutes = 25,
    onWarning,
    onTimeout,
  } = options;

  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const sessionManagerRef = useRef<SessionManager | null>(null);

  const handleTimeout = useCallback(() => {
    dispatch(signOut());
    onTimeout?.();
  }, [dispatch, onTimeout]);

  const handleWarning = useCallback(() => {
    onWarning?.();
  }, [onWarning]);

  const extendSession = useCallback(() => {
    sessionManagerRef.current?.extendSession();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Create session manager when user is authenticated
      sessionManagerRef.current = new SessionManager(
        handleTimeout,
        handleWarning
      );

      // Override timeout durations if provided
      if (timeoutMinutes !== 30 || warningMinutes !== 25) {
        sessionManagerRef.current.destroy();

        // Create session manager with custom timeouts
        sessionManagerRef.current = new SessionManager(
          handleTimeout,
          handleWarning,
          timeoutMinutes * 60 * 1000,
          warningMinutes * 60 * 1000
        );
      }
    } else {
      // Destroy session manager when user is not authenticated
      sessionManagerRef.current?.destroy();
      sessionManagerRef.current = null;
    }

    return () => {
      sessionManagerRef.current?.destroy();
    };
  }, [
    isAuthenticated,
    timeoutMinutes,
    warningMinutes,
    handleTimeout,
    handleWarning,
  ]);

  return {
    extendSession,
    isActive: !!sessionManagerRef.current,
  };
};
